import { loadStripe } from '@stripe/stripe-js';

import { createLogLocal } from '../../utils/log-local';
import {
  zeroDecimalCurrencies,
  threeDecimalCurrencies,
  SupportedPaymentMethods,
} from '../../constants';

import type { StripeElementLocale, Stripe } from '@stripe/stripe-js';
import type {
  Provider,
  PaymentMethodsType,
  StripeGatewayOptions,
} from '../../@types';

type StripeOptionsMode = 'payment' | 'setup' | 'subscription';

const log = createLogLocal('StripeProviderFactory.ts');

/**
 * Calculate amount in subunits, based on the currency. Handles 0, 2 (standard),
 * and 3 decimal currencies. Assumes the amount received is in the base unit
 * (e.g. dollars), not the subunit (e.g. cents).
 *
 * Behaviour for different currency types:
 * -   0-decimal: simply rounds (to 0 decimal places).
 * -   2-decimal (regular): multiplies by 100 (converts to "cents") & rounds.
 * -   3-decimal: multiplies by 1000 & rounds.
 */
const calcAmountInSubunits = (opts: { currency: string; amount: number }) => {
  const currency = opts?.currency?.toLowerCase?.() ?? 'usd';
  const majorUnitsAmount = +opts.amount || 0;

  // Handle 0 decimal currencies.
  if (zeroDecimalCurrencies.includes(currency)) {
    return Math.round(majorUnitsAmount);
  }
  // Handle 3-decimal currencies.
  if (threeDecimalCurrencies.includes(currency)) {
    return Math.round(majorUnitsAmount * 1000);
  }
  // Handle standard 2-decimal currencies.
  return Math.round(majorUnitsAmount * 100);
};

class StripeProviderFactory implements Provider {
  static provider: Promise<Stripe | null>;

  /**
   * Loads set of functions to interact with the payment gateway (e.g. Stripe).
   */
  private loadProvider(publishableKey: string, params?: StripeGatewayOptions) {
    log('loadProvider', 'publishableKey:', publishableKey, 'params:', params);
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (!StripeProviderFactory.provider) {
      StripeProviderFactory.provider = loadStripe(publishableKey, {
        ...(params?.configurationOptions && {
          ...params?.configurationOptions,
        }),
        ...(params?.betas && {
          betas: ['elements_enable_deferred_intent_beta_1'],
        }),
        ...(params?.locale && {
          locale: (params?.locale.replace(/[_-].*/g, '') ||
            'en') as StripeElementLocale,
        }),
        /** Examples: 'acct_1QWoR2EPZhou19AI', 'acct_1QmMh5E33ScKpb1r' */
        ...(params?.account &&
          params?.type === 'direct' && {
            stripeAccount: params.account,
          }),
      });
    }
  }

  /**
   * Build the Stripe provider.
   */
  createProvider(params?: StripeGatewayOptions) {
    log('createProvider', 'params:', params);
    const { publishableKey } = params || {};
    if (!publishableKey) throw new Error('No publishable key error.');

    this.loadProvider(publishableKey, params);

    const options: StripeGatewayOptions['options'] = { ...params?.options };

    return {
      provider: StripeProviderFactory.provider,
      getOptions: (method: PaymentMethodsType | PaymentMethodsType[]) => {
        switch (method) {
          case SupportedPaymentMethods.APPLEPAY:
          case SupportedPaymentMethods.GOOGLEPAY:
            return {
              mode: 'payment' as StripeOptionsMode,
              paymentMethodCreation: 'manual' as const,
              amount: calcAmountInSubunits(options),
              currency: options.currency || 'usd',
            };
          default:
            // 'amount' is not applicable for most methods, so remove it if exists.
            const { amount, currency, ...other } = options; // eslint-disable-line no-case-declarations
            return {
              ...other,
              ...(params?.account &&
                params?.type === 'platform' && {
                  onBehalfOf: params.account,
                }),
            };
        }
      },
    };
  }
}

/**
 * Singleton object containing "createProvider" method for creating a Stripe
 * provider.
 *
 * The Stripe provider contains a promise that resolves to the Stripe object,
 * which can be used to interact with the Stripe payment gateway.
 */
export const stripeProviderFactory = new StripeProviderFactory();
